import React, { useEffect, useState } from "react";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import {
  ReactPlugin,
  withAITracking,
  AppInsightsContext,
} from "@microsoft/applicationinsights-react-js";

const instrumentationKey =
  "InstrumentationKey=8fa34b26-2777-4cb6-92e3-1d449205d217;IngestionEndpoint=https://westus-0.in.applicationinsights.azure.com/;LiveEndpoint=https://westus.livediagnostics.monitor.azure.com/;ApplicationId=4e16504e-7684-46a7-a6c0-4886bb8530fe";

type TrackedComponent = React.ComponentType<{ children: React.ReactNode }>;

let cachedTracked: TrackedComponent | null = null;

function getOrCreateTracked(): TrackedComponent | null {
  if (typeof window === "undefined") return null;
  if (cachedTracked) return cachedTracked;

  const reactPlugin = new ReactPlugin();
  const appInsights = new ApplicationInsights({
    config: {
      connectionString: instrumentationKey,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      enableAjaxPerfTracking: true,
      isBrowserLinkTrackingEnabled: true,
      extensions: [reactPlugin],
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView();

  const AzureAppInsightsInner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>
  );

  cachedTracked = withAITracking(reactPlugin, AzureAppInsightsInner);
  return cachedTracked;
}

/** Renders children until mounted on the client, then wraps with App Insights (SSR-safe). */
const AzureAppInsights: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <>{children}</>;
  }

  const Tracked = getOrCreateTracked();
  if (!Tracked) {
    return <>{children}</>;
  }

  return <Tracked>{children}</Tracked>;
};

export default AzureAppInsights;
